/* ─── GOOGLE SHEETS (GViz) DATA SOURCE ───────────────────────────────────────
   Reads the live episode roadmaps directly from Google Sheets using the
   Google Visualization API (GViz), which requires no API key and works for
   any publicly viewable sheet ("Anyone with the link can view").

   Java roadmap sheet columns, in this exact order:
     Day | Episode No | Title | Notes Link | Problems Link | Episode Link

   Aptitude / DBMS roadmap sheet columns, in this exact order (verified
   against the actual sheet — no Problems Link column, and Notes/Episode
   Link sit one column further right than on the Java sheet):
     Day | Episode No | Episode Title | Status | Notes | Episode Link
*/

/* ─────────────────────────────────────────────────────────────────────────
   ⬇️  PASTE YOUR GOOGLE SPREADSHEET ID HERE  ⬇️
   ───────────────────────────────────────────────────────────────────────── */
export const GOOGLE_SHEET_ID = "1urYIUIg5X5DBvgUPa_14mrUoNidLRBVDORyoEFJVqyc";

/* If your data lives on a specific tab/sheet, name it here.
   Leave as-is ("Episodes") unless your tab is named differently. */
export const SHEET_NAME = "Episodes";

/* Aptitude + DBMS roadmaps live as separate tabs inside the same
   "RecordingsFSD" workbook. */
export const APTITUDE_DBMS_SHEET_ID = "1p681tCyY-Dr1uYehBOhTgun47GPTOL44xhWeCYrZUAo";
export const APTITUDE_SHEET_NAME = "Sheet3";
export const DBMS_SHEET_NAME = "Sheet4";

/**
 * Builds the GViz endpoint URL for the configured spreadsheet + sheet/tab.
 * `tqx=out:json` tells Google to return the response as JSON (technically
 * wrapped — see parseGvizResponse below).
 */
function buildGvizUrl(sheetId = GOOGLE_SHEET_ID, sheetName = SHEET_NAME) {
  const base = `https://docs.google.com/spreadsheets/d/${sheetId}/gviz/tq`;
  const params = new URLSearchParams({ tqx: "out:json", sheet: sheetName });
  return `${base}?${params.toString()}`;
}

/**
 * Google's GViz endpoint doesn't return plain JSON — it wraps the JSON
 * payload in a JS function call, e.g.:
 *   google.visualization.Query.setResponse({ ...actual json... });
 * We strip that wrapper so we can JSON.parse the inner object safely.
 */
function unwrapGvizResponse(rawText) {
  const match = rawText.match(/google\.visualization\.Query\.setResponse\(([\s\S]*)\);?\s*$/);
  const jsonString = match ? match[1] : rawText;
  return JSON.parse(jsonString);
}

/**
 * Safely reads a cell's value. GViz represents an empty cell as `null`
 * (not an object), so we guard against that and fall back to "".
 */
function cellValue(cell) {
  return cell ? cell.v ?? "" : "";
}

/**
 * Shared fetch + unwrap step used by every roadmap fetcher below.
 */
async function fetchGvizJson(sheetId, sheetName) {
  const url = buildGvizUrl(sheetId, sheetName);

  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Google Sheets request failed with status ${res.status}`);
  }

  const rawText = await res.text();
  return unwrapGvizResponse(rawText);
}

/**
 * Converts the raw GViz table (`{ table: { rows: [...] } }`) into the same
 * flat array-of-objects shape the rest of the app expects, using the column
 * order: Day | Episode No | Title | Notes Link | Problems Link | Episode Link.
 *
 * NOTE: column order in the sheet maps positionally to indices 0–5 below,
 * so reordering columns in the sheet itself would require updating these
 * indices.
 */
function parseJavaGvizRows(gvizJson) {
  const rows = gvizJson?.table?.rows ?? [];

  return rows.map((row) => {
    const cells = row.c || [];
    return {
      day: cellValue(cells[0]),
      episodeNo: cellValue(cells[1]),
      title: cellValue(cells[2]),
      notesLink: cellValue(cells[3]),
      problemsLink: cellValue(cells[4]),
      episodeLink: cellValue(cells[5]),
    };
  });
}

/**
 * Same idea as parseJavaGvizRows, but for the Aptitude/DBMS sheet layout:
 * Day | Episode No | Episode Title | Status | Notes | Episode Link.
 * There's no Problems Link column on these sheets, so that field is simply
 * omitted from the returned row objects (RoadmapModal already skips any
 * field that's missing/NA).
 */
function parseAptitudeDbmsGvizRows(gvizJson) {
  const rows = gvizJson?.table?.rows ?? [];

  return rows.map((row) => {
    const cells = row.c || [];
    return {
      day: cellValue(cells[0]),
      episodeNo: cellValue(cells[1]),
      title: cellValue(cells[2]),
      status: cellValue(cells[3]),
      notesLink: cellValue(cells[4]),
      episodeLink: cellValue(cells[5]),
    };
  });
}

/**
 * Public fetch function — drop-in replacement for the old SheetDB fetch.
 * Returns an array of episode objects:
 *   { day, episodeNo, title, notesLink, problemsLink, episodeLink }
 *
 * Throws on network/parse failure so callers can handle it with their
 * existing try/catch + error UI.
 */
export async function fetchJavaRoadmapEpisodes(sheetId = GOOGLE_SHEET_ID, sheetName = SHEET_NAME) {
  const gvizJson = await fetchGvizJson(sheetId, sheetName);
  return parseJavaGvizRows(gvizJson);
}

/**
 * Fetches the Aptitude roadmap (Sheet3 of the RecordingsFSD workbook).
 * Returns an array of episode objects:
 *   { day, episodeNo, title, status, notesLink, episodeLink }
 */
export async function fetchAptitudeRoadmapEpisodes(
  sheetId = APTITUDE_DBMS_SHEET_ID,
  sheetName = APTITUDE_SHEET_NAME
) {
  const gvizJson = await fetchGvizJson(sheetId, sheetName);
  return parseAptitudeDbmsGvizRows(gvizJson);
}

/**
 * Fetches the DBMS roadmap (Sheet4 of the RecordingsFSD workbook).
 * Returns an array of episode objects:
 *   { day, episodeNo, title, status, notesLink, episodeLink }
 */
export async function fetchDbmsRoadmapEpisodes(
  sheetId = APTITUDE_DBMS_SHEET_ID,
  sheetName = DBMS_SHEET_NAME
) {
  const gvizJson = await fetchGvizJson(sheetId, sheetName);
  return parseAptitudeDbmsGvizRows(gvizJson);
}